/* Created by Interactive 63 - https://interactive63.com */
/**
 * The ruler above the keyframe area: tick marks and labels, the playhead handle, the draggable
 * end-of-timeline handle, scrubbing, and slider semantics for keyboard and screen reader users.
 */
import { preferences } from '../../preferences';
import { setDuration } from '../../store/property-actions';
import { formatTime } from '../../time-format';
import { APPEARANCE_EVENT } from '../../appearance';
import { h } from '../../ui/dom';
import type { TimelineHost } from './host';
import { computeTicks, timeToX, xToTime } from './ruler';

const HEIGHT = 24;

/** Reads a CSS custom property from the document root. */
function cssVar(name: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

export class RulerView {
  readonly el: HTMLElement;
  private readonly canvas: HTMLCanvasElement;
  private readonly playheadHandle: HTMLElement;
  private readonly endHandle: HTMLElement;

  /** Builds the ruler. */
  constructor(private readonly host: TimelineHost) {
    window.addEventListener(APPEARANCE_EVENT, () => {
      this.draw();
    });
    this.canvas = h('canvas', { class: 'ruler-canvas' });
    this.playheadHandle = h('div', { class: 'ruler-playhead', 'aria-hidden': 'true' });
    this.endHandle = h('div', {
      class: 'end-handle',
      title: 'Drag to change the scene duration (Alt inverts "Scale keyframes")',
    });
    this.el = h(
      'div',
      {
        class: 'ruler',
        role: 'slider',
        tabindex: 0,
        'aria-label': 'Playhead',
        'aria-valuemin': 0,
      },
      this.canvas,
      this.playheadHandle,
      this.endHandle,
    );
    this.bindScrub();
    this.bindEndHandle();
    this.bindKeyboard();
  }

  /** Time under a pointer event, clamped to the scene. */
  private timeAt(event: PointerEvent): number {
    const rect = this.el.getBoundingClientRect();
    const duration = this.host.store.getState().project.scene.duration;
    return Math.min(
      duration,
      Math.max(0, Math.round(xToTime(event.clientX - rect.left, this.host.getView()))),
    );
  }

  /** Lets the ruler be clicked or dragged to scrub, pausing playback first. */
  private bindScrub(): void {
    this.el.addEventListener('pointerdown', (down) => {
      if (down.button !== 0 || down.target === this.endHandle) return;
      down.preventDefault();
      this.el.focus();
      if (this.host.playback.isPlaying) this.host.playback.pause();
      this.el.setPointerCapture(down.pointerId);
      let latest = this.timeAt(down);
      this.host.playback.scrub(latest);

      /** Seeks live while dragging. */
      const move = (event: PointerEvent): void => {
        latest = this.timeAt(event);
        this.host.playback.scrub(latest);
      };
      /** Records the final playhead in the store. */
      const end = (): void => {
        this.el.removeEventListener('pointermove', move);
        this.el.removeEventListener('pointerup', end);
        this.el.removeEventListener('pointercancel', end);
        this.host.playback.seek(latest);
      };
      this.el.addEventListener('pointermove', move);
      this.el.addEventListener('pointerup', end);
      this.el.addEventListener('pointercancel', end);
    });
  }

  /** Lets the end handle be dragged to change the duration, committing once on release. */
  private bindEndHandle(): void {
    this.endHandle.addEventListener('pointerdown', (down) => {
      if (down.button !== 0) return;
      down.preventDefault();
      down.stopPropagation();
      this.endHandle.setPointerCapture(down.pointerId);

      /** Time under the pointer, rounded and kept within the allowed range. */
      const durationAt = (event: PointerEvent): number => {
        const rect = this.el.getBoundingClientRect();
        return Math.round(xToTime(event.clientX - rect.left, this.host.getView()) / 10) * 10;
      };
      /** Previews the new duration. */
      const move = (event: PointerEvent): void => {
        this.host.setPendingDuration(Math.max(100, durationAt(event)));
      };
      /** Applies the new duration, scaling keyframes unless Alt inverts the preference. */
      const end = (event: PointerEvent): void => {
        this.endHandle.removeEventListener('pointermove', move);
        this.endHandle.removeEventListener('pointerup', end);
        this.endHandle.removeEventListener('pointercancel', end);
        const target = Math.max(100, durationAt(event));
        this.host.setPendingDuration(null);
        setDuration(this.host.store, target, {
          scale: event.altKey ? !preferences.scaleKeyframes : preferences.scaleKeyframes,
          confirmRemoval: (lost) =>
            window.confirm(
              `${lost} keyframe${lost === 1 ? ' is' : 's are'} after the new end time and will be removed. Continue?`,
            ),
        });
      };
      this.endHandle.addEventListener('pointermove', move);
      this.endHandle.addEventListener('pointerup', end);
      this.endHandle.addEventListener('pointercancel', end);
    });
  }

  /** Arrow keys nudge the playhead by 10 ms (100 ms with Shift); Home and End jump. */
  private bindKeyboard(): void {
    this.el.addEventListener('keydown', (event) => {
      const step = event.shiftKey ? 100 : 10;
      const now = this.host.playback.currentTime;
      const duration = this.host.store.getState().project.scene.duration;
      let target: number | null = null;
      if (event.key === 'ArrowLeft') target = now - step;
      else if (event.key === 'ArrowRight') target = now + step;
      else if (event.key === 'Home') target = 0;
      else if (event.key === 'End') target = duration;
      if (target === null) return;
      event.preventDefault();
      this.host.playback.seek(target);
    });
  }

  /** Moves the playhead handle and updates the slider values. */
  setPlayhead(ms: number): void {
    const duration = this.host.store.getState().project.scene.duration;
    const x = timeToX(ms, this.host.getView());
    this.playheadHandle.style.transform = `translateX(${x}px)`;
    this.playheadHandle.hidden = x < -8 || x > this.host.getWidth() + 8;
    this.el.setAttribute('aria-valuemax', String(duration));
    this.el.setAttribute('aria-valuenow', String(Math.round(ms)));
    this.el.setAttribute(
      'aria-valuetext',
      `${Number((ms / 1000).toFixed(2))} seconds (${formatTime(ms)})`,
    );
  }

  /** Redraws ticks and positions the end handle. */
  draw(): void {
    const width = this.host.getWidth();
    const dpr = window.devicePixelRatio || 1;
    if (this.canvas.width !== Math.round(width * dpr) || this.canvas.height !== HEIGHT * dpr) {
      this.canvas.width = Math.round(width * dpr);
      this.canvas.height = HEIGHT * dpr;
      this.canvas.style.width = `${width}px`;
      this.canvas.style.height = `${HEIGHT}px`;
    }
    const g = this.canvas.getContext('2d');
    if (!g) return;
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.clearRect(0, 0, width, HEIGHT);

    const view = this.host.getView();
    const { major, minor } = computeTicks(view, width);
    g.strokeStyle = cssVar('--border');
    g.fillStyle = cssVar('--text-muted');
    g.font = `11px ${cssVar('--font-ui') || 'sans-serif'}`;
    g.textBaseline = 'top';
    g.beginPath();
    for (const x of minor) {
      g.moveTo(Math.round(x) + 0.5, HEIGHT - 5);
      g.lineTo(Math.round(x) + 0.5, HEIGHT);
    }
    for (const tick of major) {
      g.moveTo(Math.round(tick.x) + 0.5, HEIGHT - 10);
      g.lineTo(Math.round(tick.x) + 0.5, HEIGHT);
      g.fillText(tick.label, tick.x + 3, 3);
    }
    g.stroke();

    const duration =
      this.host.getPendingDuration() ?? this.host.store.getState().project.scene.duration;
    const endX = timeToX(duration, view);
    this.endHandle.style.transform = `translateX(${endX}px)`;
    this.endHandle.hidden = endX < -8 || endX > width + 8;
  }
}
